import { useState } from "react";
import type { ProjectTree, SavedFilter } from "../api/generated";
import type { SyncStatus } from "../api/taskApi";
import type { TagCount } from "../domain/tags";
import { pathKey, subtreePendingCount, visibleProjectRows } from "../domain/projects";
import {
  BUILTIN_SHORTCUTS,
  builtinShortcut,
  projectShortcut,
  sameQuery,
  savedFilterShortcut,
  tagShortcut,
  type BuiltinShortcutId,
  type ListQuery,
  type Shortcut,
} from "../domain/views";
import type { ThemePreference } from "../state/settings";
import { SyncIndicator } from "./SyncIndicator";

const BUILTIN_GLYPHS: Record<BuiltinShortcutId, string> = {
  inbox: "▤",
  urgent: "◉",
  next: "➜",
  done: "✓",
};

interface SidebarProps {
  open: boolean;
  /** The list query in effect; an entry is highlighted exactly when its preset equals it. */
  query: ListQuery;
  projects: ProjectTree;
  savedFilters: SavedFilter[];
  /** Tags used by open tasks. */
  tags: TagCount[];
  /** Open tasks without a project; shown as a badge on the Inbox entry. */
  inbox: number;
  syncStatus: SyncStatus;
  theme: ThemePreference;
  onSelectShortcut(shortcut: Shortcut): void;
  onSyncNow(): void;
  onCycleTheme(): void;
  onOpenSettings(): void;
}

export function Sidebar({
  open,
  query,
  projects,
  savedFilters,
  tags,
  inbox,
  syncStatus,
  theme,
  onSelectShortcut,
  onSyncNow,
  onCycleTheme,
  onOpenSettings,
}: SidebarProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [tagsOpen, setTagsOpen] = useState(true);
  const rows = visibleProjectRows(projects, collapsed);
  const isCurrent = (shortcut: Shortcut) => sameQuery(query, shortcut.query);

  const toggle = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  return (
    <nav className={`sidebar${open ? " open" : ""}`} aria-label="Views">
      <div className="brand">
        <span className="brand-mark" aria-hidden="true">
          ✓
        </span>
        td
      </div>

      {BUILTIN_SHORTCUTS.map(({ id, title }) => {
        const target = builtinShortcut(id);
        return (
          <button
            key={id}
            type="button"
            className="nav-item"
            aria-current={isCurrent(target) ? "page" : undefined}
            onClick={() => onSelectShortcut(target)}
          >
            <span className="nav-glyph" aria-hidden="true">
              {BUILTIN_GLYPHS[id]}
            </span>
            <span className="nav-label">{title}</span>
            {id === "inbox" && inbox > 0 && <span className="nav-count">{inbox}</span>}
          </button>
        );
      })}

      {savedFilters.length > 0 && (
        <>
          <div className="nav-heading">Filters</div>
          {savedFilters.map((filter) => {
            const target = savedFilterShortcut(filter);
            return (
              <button
                key={filter.name}
                type="button"
                className="nav-item"
                title={filter.expression}
                aria-current={isCurrent(target) ? "page" : undefined}
                onClick={() => onSelectShortcut(target)}
              >
                <span className="nav-glyph" aria-hidden="true">
                  ⧩
                </span>
                <span className="nav-label">{filter.name}</span>
              </button>
            );
          })}
        </>
      )}

      {tags.length > 0 && (
        <>
          <button
            type="button"
            className="nav-heading nav-heading-toggle"
            aria-expanded={tagsOpen}
            onClick={() => setTagsOpen((current) => !current)}
          >
            <span>Tags</span>
            <span aria-hidden="true">{tagsOpen ? "▾" : "▸"}</span>
          </button>
          {tagsOpen &&
            tags.map(({ tag, count }) => {
              const target = tagShortcut(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  className="nav-item"
                  title={tag}
                  aria-label={`Tag ${tag}, ${count} open`}
                  aria-current={isCurrent(target) ? "page" : undefined}
                  onClick={() => onSelectShortcut(target)}
                >
                  <span className="nav-glyph" aria-hidden="true">
                    #
                  </span>
                  <span className="nav-label">{tag}</span>
                  <span className="nav-count">{count}</span>
                </button>
              );
            })}
        </>
      )}

      <div className="nav-heading">Projects</div>
      {rows.length === 0 && <div className="hint nav-label">No projects yet</div>}
      {rows.map(({ node, depth }) => {
        const key = pathKey(node.path);
        const target = projectShortcut(node.path);
        const expandable = node.children.length > 0;
        const isCollapsed = collapsed.has(key);
        return (
          <div key={key} className="nav-row" style={{ paddingLeft: depth * 14 }}>
            {expandable ? (
              <button
                type="button"
                className="tree-toggle"
                aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${node.name}`}
                aria-expanded={!isCollapsed}
                onClick={() => toggle(key)}
              >
                {isCollapsed ? "▸" : "▾"}
              </button>
            ) : (
              <span className="tree-spacer" />
            )}
            <button
              type="button"
              className="nav-item"
              aria-current={isCurrent(target) ? "page" : undefined}
              onClick={() => onSelectShortcut(target)}
            >
              <span className="nav-label">{node.name}</span>
              <span className="nav-count">{subtreePendingCount(node)}</span>
            </button>
          </div>
        );
      })}

      <div className="sidebar-footer">
        <SyncIndicator status={syncStatus} onSyncNow={onSyncNow} />
        <button type="button" className="nav-item" onClick={onCycleTheme}>
          <span className="nav-glyph" aria-hidden="true">
            ◐
          </span>
          <span className="nav-label">Theme: {theme}</span>
        </button>
        <button type="button" className="nav-item" onClick={onOpenSettings}>
          <span className="nav-glyph" aria-hidden="true">
            ⚙
          </span>
          <span className="nav-label">Settings</span>
        </button>
      </div>
    </nav>
  );
}
